import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { type DeepPartialSkipArrayKey, FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { AdminApprovalType, type UpdateCustomRoleRequest } from '@column/api';
import { Alert, AlertDescription, AlertTitle, Button } from '@column/ui';

import { buildUpdateRequestForEntitiesAndAccountsSubset } from '../utils';
import { StateTabs } from '~/app/Layout';
import { useBreadcrumbTitle } from '~/app/Layout/PageBreadcrumbs';
import { PageActions } from '~/app/Layout/PageFrameContext';
import { ReasonTooltip } from '~/app/pages/Platform/Roles/_partials/ReasonTooltip';
import { RoleInfo } from '~/app/pages/Platform/Roles/_partials/RoleInfo';
import { ROUTE } from '~/app/routes';
import { Loading } from '~/components';
import { useDialog } from '~/components/Dialog/Provider';
import { useFormErrorHandler } from '~/hooks/useFormErrorHandler';
import { useDeleteCustomRole, useGetCustomRole, useUpdateCustomRole } from '~/hooks/useRoles';
import { AdminApprovalReviewDialog, invalidateAdminApprovals, usePendingAdminApprovals } from '~/lib/approvals';
import { useNavigate } from '~/lib/navigation';
import PermissionConfiguration, { Mode, PermissionConfigurationSection, useEntitiesAndAccounts } from '~/lib/roles';
import { RoleRepository, getRoleLabel } from '~/repositories/RoleRepository';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';

enum Section {
  Info = 'role-info',
  Transfers = 'role-transfers-and-accounts',
  Additional = 'role-additional-permissions',
}

const sections = [
  { label: 'Role Info', value: Section.Info },
  { label: 'Transfers & Accounts', value: Section.Transfers },
  { label: 'Additional Permissions', value: Section.Additional },
];
const sectionValues = sections.map((section) => section.value);

const trimData = (
  data: DeepPartialSkipArrayKey<KeysCamelCaseDeep<UpdateCustomRoleRequest>>
): DeepPartialSkipArrayKey<KeysCamelCaseDeep<UpdateCustomRoleRequest>> => {
  const output: DeepPartialSkipArrayKey<KeysCamelCaseDeep<UpdateCustomRoleRequest>> = {};

  output.name = data.name;
  output.description = data.description ? data.description : '';

  if (data.platformLevelPermissions) {
    output.platformLevelPermissions = Object.fromEntries(
      Object.entries(data.platformLevelPermissions).filter(([, v]) => v !== 'default')
    );
  }
  if (data.entityLevelOverrides) {
    output.entityLevelOverrides = (data.entityLevelOverrides ?? [])
      .map((entity) => {
        return Object.fromEntries(Object.entries(entity).filter(([k, v]) => k === 'entityId' || v !== 'default'));
      })
      .filter((entity) => Object.keys(entity).length > 1);
  }
  if (data.bankAccountLevelOverrides) {
    output.bankAccountLevelOverrides = (data.bankAccountLevelOverrides ?? [])
      .map((bankAccount) => {
        return Object.fromEntries(
          Object.entries(bankAccount).filter(([k, v]) => k === 'bankAccountId' || v !== 'default')
        );
      })
      .filter((entity) => Object.keys(entity).length > 1);
  }

  return output;
};

const ROLE_UPDATE_PENDING_MESSAGE = 'Role changes submitted for approval.';
const MANAGE_ROLES_PERMISSION_TOOLTIP = 'You do not have permission to manage roles.';
const ASSIGNED_ROLE_DELETE_TOOLTIP =
  'This role is currently assigned to users. It cannot be deleted until it is unassigned.';

export const PageEditRole: React.FC = () => {
  const { currentPlatform, isAuthorized } = useSessionStore();
  const canWriteRoles = isAuthorized({ permission: 'users', permissionLevel: 'write' });
  const { roleId } = useParams();
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const navigate = useNavigate();
  const { openAlert } = useDialog();
  const { handleSubmitErrors } = useFormErrorHandler();
  const [isReviewOpen, setIsReviewOpen] = useState(false);
  const queryClient = useQueryClient();
  const pendingRoleUpdates = usePendingAdminApprovals(AdminApprovalType.ROLE_UPDATE);
  const pendingApprovals = useMemo(
    () => pendingRoleUpdates.filter((a) => a.request.role_update?.role_id === roleId),
    [pendingRoleUpdates, roleId]
  );
  const pendingApproval = pendingApprovals[0];

  const {
    createRequest: getRole,
    isLoading: isRoleLoading,
    response: role,
  } = useGetCustomRole({
    initialParams: roleId ? { roleId } : undefined,
    shouldNotLoad: !roleId,
    onError: (error) => {
      addDangerNotification({ content: error.message });
    },
  });
  useEffect(() => {
    if (roleId) {
      getRole({ roleId });
    }
  }, [roleId]);

  const updateRole = useUpdateCustomRole({
    onSuccess: (response) => {
      addSuccessNotification({
        content: response.pendingApproval ? ROLE_UPDATE_PENDING_MESSAGE : 'Role updated successfully',
      });
      invalidateAdminApprovals(queryClient);
    },
    onError: (error) => {
      addDangerNotification({ content: error.message });
    },
  });
  const deleteRole = useDeleteCustomRole({
    onSuccess: () => {
      addSuccessNotification({ content: 'Role deleted successfully' });
      navigate(ROUTE.PLATFORM_ROLES);
    },
    onError: (error) => {
      addDangerNotification({ content: error.message });
    },
  });

  const existingBankAccountIDs = useMemo(
    () => role?.bankAccountLevelOverrides?.map((ba) => ba.bankAccountId).filter((id) => typeof id === 'string') ?? [],
    [role]
  );
  const existingEntityIDs = useMemo(
    () => role?.entityLevelOverrides?.map((e) => e.entityId).filter((id) => typeof id === 'string') ?? [],
    [role]
  );

  const sandboxEntitiesAndAccounts = useEntitiesAndAccounts({
    bankAccountIDs: existingBankAccountIDs,
    entityIds: existingEntityIDs,
    mode: Mode.Sandbox,
  });
  const liveEntitiesAndAccounts = useEntitiesAndAccounts({
    bankAccountIDs: existingBankAccountIDs,
    entityIds: existingEntityIDs,
    mode: Mode.Live,
  });

  const [visibleSection, setVisibleSection] = useState<Section>(Section.Info);
  useEffect(() => {
    if (!sectionValues.includes(visibleSection)) {
      setVisibleSection(Section.Info);
    }
  }, [visibleSection]);

  useBreadcrumbTitle(role ? (role.editable ? role.name : getRoleLabel(role.name)) : null);

  const handleCancel = useCallback(() => {
    navigate(ROUTE.PLATFORM_ROLES);
  }, []);
  const handleDelete = useCallback(() => {
    if (!roleId) return;
    openAlert({
      headline: 'Delete role',
      content: 'Are you sure you want to delete this role? This action cannot be reversed.',
      confirmButton: {
        label: 'Delete Role',
        variant: 'danger',
      },
      onSuccess: () => {
        deleteRole.createRequest({ roleId });
      },
    });
  }, [deleteRole, openAlert, roleId]);

  const deleteReason = !canWriteRoles
    ? MANAGE_ROLES_PERMISSION_TOOLTIP
    : (role?.userCount ?? 0) > 0
      ? ASSIGNED_ROLE_DELETE_TOOLTIP
      : undefined;
  const saveReason = !canWriteRoles
    ? MANAGE_ROLES_PERMISSION_TOOLTIP
    : pendingApproval
      ? 'Resolve the pending change before editing this role.'
      : undefined;

  const form = useForm<DeepPartialSkipArrayKey<KeysCamelCaseDeep<UpdateCustomRoleRequest>>>({
    values: role,
  });
  useEffect(() => {
    form.reset();
  }, [role]);

  const handleSubmit = form.handleSubmit(
    (data: DeepPartialSkipArrayKey<KeysCamelCaseDeep<UpdateCustomRoleRequest>>) => {
      if (!roleId) return;
      const trimmedData = trimData(data);

      // Due to some limitations in the API (we can't add both sandbox and live entities/accounts in the same request,
      // we must perform two updates to 1. set the sandbox entities/accounts then 2. set the live entities/accounts.
      if (currentPlatform?.isLiveEnabled) {
        const sandboxData = buildUpdateRequestForEntitiesAndAccountsSubset(trimmedData, sandboxEntitiesAndAccounts);
        RoleRepository.update({ roleId, mode: 'sandbox', ...sandboxData })
          .then((response) => {
            if (!response.pendingApproval) return updateRole.createRequest({ roleId, mode: 'live', ...trimmedData });
            addSuccessNotification({ content: ROLE_UPDATE_PENDING_MESSAGE });
            return invalidateAdminApprovals(queryClient);
          })
          .catch((err) => addDangerNotification({ content: err.message }));
      } else {
        updateRole.createRequest({ roleId, mode: 'sandbox', ...trimmedData });
      }
    },
    (errors) => {
      // Role Info holds the only required field, so send the user back to fix it.
      setVisibleSection(Section.Info);
      handleSubmitErrors(errors);
    }
  );

  return (
    <Loading isLoading={isRoleLoading || role === undefined}>
      <StateTabs
        value={visibleSection}
        onValueChange={(section) => setVisibleSection(section as Section)}
        entries={sections}
      />
      <PageActions>
        <ReasonTooltip reason={deleteReason}>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            className="text-destructive dark:text-red-400"
            disabled={!!deleteReason}
            onClick={handleDelete}
          >
            Delete Role
          </Button>
        </ReasonTooltip>
        <Button type="button" size="sm" variant="secondary" onClick={handleCancel}>
          Cancel
        </Button>
        <ReasonTooltip reason={saveReason}>
          <Button
            type="button"
            size="sm"
            disabled={!!saveReason || (!updateRole.isInitialLoad && updateRole.isLoading)}
            onClick={handleSubmit}
          >
            Save Role
          </Button>
        </ReasonTooltip>
      </PageActions>
      {pendingApproval && (
        <div className="px-[var(--page-margin)] pt-4 [&+*]:!shadow-none">
          <Alert className="flex items-center justify-between gap-4">
            <div>
              <AlertTitle>
                {pendingApproval.can_review ? 'Changes to this role need your review' : 'Changes pending approval'}
              </AlertTitle>
              <AlertDescription>
                Requested {new Date(pendingApproval.created_at).toLocaleString()}. This role can't be edited until the
                change is approved, rejected, or expires.
              </AlertDescription>
            </div>
            <Button
              type="button"
              size="sm"
              variant={pendingApproval.can_review ? 'primary' : 'secondary'}
              onClick={() => setIsReviewOpen(true)}
            >
              {pendingApproval.can_review ? 'Review' : 'View'}
            </Button>
          </Alert>
        </div>
      )}
      <AdminApprovalReviewDialog
        approvals={pendingApprovals}
        startIndex={0}
        hideCounter
        isOpen={isReviewOpen && !!pendingApproval}
        onClose={() => setIsReviewOpen(false)}
        onReviewed={() => roleId && getRole({ roleId })}
      />
      <FormProvider {...form}>
        {/* Every section stays mounted so validation still sees fields on the hidden ones. */}
        <div hidden={visibleSection !== Section.Info}>
          <RoleInfo />
        </div>
        <div hidden={visibleSection !== Section.Transfers}>
          <PermissionConfiguration
            visibleSection={PermissionConfigurationSection.Transfers}
            sandboxEntitiesAndAccounts={sandboxEntitiesAndAccounts}
            liveEntitiesAndAccounts={liveEntitiesAndAccounts}
          />
        </div>
        <div hidden={visibleSection !== Section.Additional}>
          <PermissionConfiguration
            visibleSection={PermissionConfigurationSection.Additional}
            sandboxEntitiesAndAccounts={sandboxEntitiesAndAccounts}
            liveEntitiesAndAccounts={liveEntitiesAndAccounts}
          />
        </div>
      </FormProvider>
    </Loading>
  );
};
